import { useState, useEffect, useRef, useCallback } from "react";
import { Star, Quote, MapPin, X, ChevronLeft, ChevronRight } from "lucide-react";
import api from "../services/api";
import { toast } from "sonner";

const serviceLabels = {
  delivery: "Standard Delivery",
  room_shifting: "Room Shifting",
  office_shifting: "Office Shifting",
  luggage_shifting: "Luggage Shifting",
  small_furniture: "Small Furniture",
};

function StarRating({ score }) {
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star
          key={`star-${i}`}
          className={`h-3.5 w-3.5 ${i < score ? "fill-amber-400 text-amber-400" : "fill-slate-200 text-slate-200"}`}
        />
      ))}
    </div>
  );
}

function ReviewCard({ review, isAdmin, onDelete }) {
  const name = review.user_name || "Customer";
  const initials = name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const colors = ["bg-[#FF6B00]", "bg-violet-600", "bg-emerald-600", "bg-blue-600", "bg-rose-600"];
  const bgColor = colors[name.charCodeAt(0) % colors.length];

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm hover:shadow-md hover:border-slate-200 transition-all duration-300 flex flex-col h-full" data-testid="review-card">
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-10 h-10 ${bgColor} rounded-xl flex items-center justify-center text-white text-xs font-bold shrink-0`}>
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900 truncate">{name}</p>
          <StarRating score={review.rating?.score || 5} />
        </div>
        <Quote className="h-5 w-5 text-[#FF6B00]/20 shrink-0" />
        {isAdmin && (
          <button onClick={() => onDelete(review.tracking_id)} className="p-1 rounded-lg hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors" data-testid={`delete-review-${review.tracking_id}`}>
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <p className="text-sm text-slate-600 leading-relaxed flex-1 mb-3">"{review.rating?.comment}"</p>
      <div className="flex items-center gap-2 text-[10px] text-slate-400 pt-2 border-t border-slate-50">
        <span className="inline-flex items-center gap-1">
          <MapPin className="h-2.5 w-2.5" />
          {(review.pickup_address || "").split(",")[0]} → {(review.delivery_address || "").split(",")[0]}
        </span>
        {review.service_type && review.service_type !== "delivery" && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-orange-50 text-[#FF6B00] rounded font-medium">
            {serviceLabels[review.service_type] || review.service_type}
          </span>
        )}
      </div>
    </div>
  );
}

const AUTO_CYCLE_MS = 4000;

export default function CustomerReviews({ isAdmin = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef(null);

  const fetchReviews = () => {
    api.get("/public/reviews")
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchReviews(); }, []);

  const reviews = data?.reviews || [];
  const n = reviews.length;

  const scrollToCard = useCallback((i) => {
    const track = trackRef.current;
    if (!track || !track.children.length) return;
    const card = track.children[Math.min(i, track.children.length - 1)];
    track.scrollTo({ left: card.offsetLeft - track.offsetLeft, behavior: "smooth" });
  }, []);

  // Auto-cycle: advance one card, wrap to start at the end.
  useEffect(() => {
    if (paused || n < 2) return;
    const t = setInterval(() => {
      setIdx((prev) => {
        const track = trackRef.current;
        if (!track) return prev;
        const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 8;
        const next = atEnd ? 0 : prev + 1;
        scrollToCard(next);
        return next;
      });
    }, AUTO_CYCLE_MS);
    return () => clearInterval(t);
  }, [paused, n, scrollToCard]);

  const step = (dir) => {
    const next = Math.max(0, Math.min(n - 1, idx + dir));
    setIdx(next);
    scrollToCard(next);
  };

  const handleDelete = async (trackingId) => {
    try {
      await api.delete(`/admin/reviews/${trackingId}`);
      toast.success("Review removed");
      fetchReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove");
    }
  };

  if (loading || !data || n === 0) return null;

  return (
    <section className="py-16 sm:py-20 bg-gradient-to-b from-slate-50/50 to-white" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-50 border border-amber-200 rounded-full mb-4">
            <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
            <span className="text-sm font-semibold text-amber-700">{data.avg_rating} / 5 average rating</span>
          </div>
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">
            What Our Customers Say
          </h2>
          <p className="text-sm text-slate-500 mt-2">{data.total_reviews} verified reviews from real deliveries</p>
        </div>

        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="reviews-carousel"
        >
          <div
            ref={trackRef}
            className="flex gap-5 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            data-testid="reviews-carousel-track"
          >
            {reviews.map((review, i) => (
              <div
                key={review.tracking_id || review.rating?.created_at || i}
                className="w-full sm:w-[calc(50%-10px)] lg:w-[calc(33.333%-14px)] shrink-0 snap-start"
              >
                <ReviewCard review={review} isAdmin={isAdmin} onDelete={handleDelete} />
              </div>
            ))}
          </div>

          {n > 1 && (
            <>
              <button
                type="button"
                onClick={() => step(-1)}
                disabled={idx === 0}
                aria-label="Previous review"
                className="absolute -left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-500 hover:text-[#FF6B00] hover:border-orange-200 transition-colors disabled:opacity-30 disabled:pointer-events-none"
                data-testid="reviews-prev-btn"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => step(1)}
                disabled={idx >= n - 1}
                aria-label="Next review"
                className="absolute -right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-500 hover:text-[#FF6B00] hover:border-orange-200 transition-colors disabled:opacity-30 disabled:pointer-events-none"
                data-testid="reviews-next-btn"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </>
          )}

          {n > 1 && (
            <div className="flex items-center justify-center gap-1.5 mt-6" data-testid="reviews-dots">
              {reviews.map((_, i) => (
                <button
                  key={`dot-${i}`}
                  type="button"
                  aria-label={`Go to review ${i + 1}`}
                  onClick={() => { setIdx(i); scrollToCard(i); }}
                  className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-6 bg-[#FF6B00]" : "w-1.5 bg-slate-300 hover:bg-slate-400"}`}
                  data-testid={`reviews-dot-${i}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
